import { useCallback, useEffect, useState } from 'react';
import { renderFarmWaitHint } from '../lib/render-farm-capacity';

interface NodeJob { id: string; module: string; status: string; progress: number }
interface Node {
  id: string; name: string; status: string; operationalStatus: string; lastHeartbeat: string | null;
  hostname: string | null; version: string | null; capabilities: string[];
  activeJobs: number; maxConcurrency: number; availableSlots: number | null;
  shortAvailableSlots: number | null; memoryReserveBytes: number | null; memoryPerRenderBytes: number | null;
  shortMemoryPerRenderBytes: number | null; minFreeDiskBytes: number | null;
  memoryFreeBytes: number | null; diskFreeBytes: number | null; loadAverage: number | null;
  currentJobs: NodeJob[];
}
interface Job { id: string; module: string; status: string; videoMode: string; progress: number; rendererId: string | null; error: string | null; createdAt: string }

const badge = (s: string) =>
  s === 'COMPLETED' || s === 'SUCCEEDED' ? 'badge badge--success'
  : s === 'FAILED' || s === 'CANCELLED' ? 'badge badge--danger'
  : s === 'PROCESSING' || s === 'RENDERING' || s === 'QUEUED' ? 'badge badge--warning'
  : 'badge badge--neutral';
const nodeBadge = (s: string) => s === 'IDLE' ? 'badge badge--success'
  : s === 'BUSY' || s === 'WAITING_FOR_RESOURCES' || s === 'SHORTS_ONLY' ? 'badge badge--warning'
  : 'badge badge--danger';
const nodeStatusLabel = (s: string) => s === 'IDLE' ? 'GOTOWY'
  : s === 'BUSY' ? 'RENDERUJE'
  : s === 'SHORTS_ONLY' ? 'SHORTY MOŻLIWE'
  : s === 'WAITING_FOR_RESOURCES' ? 'CZEKA NA ZASOBY'
  : 'OFFLINE';
const formatBytes = (value: number | null) => value == null || !Number.isFinite(value) ? '—' : `${(value / 1024 ** 3).toFixed(1)} GiB`;
const formatTimestamp = (value: string) => {
  const timestamp = new Date(value);
  return Number.isNaN(timestamp.getTime()) ? '—' : timestamp.toLocaleString();
};
const errorPreview = (value: string) => {
  const firstLine = (value.split(/\r?\n/, 1)[0] ?? '').replace(/\s+/g, ' ').trim();
  return firstLine.length > 100 ? `${firstLine.slice(0, 99)}…` : firstLine;
};

export function RenderFarmPage() {
  const [nodes, setNodes] = useState<Node[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobIdSearch, setJobIdSearch] = useState('');
  const [searchedJob, setSearchedJob] = useState<Job | null>(null);
  const [searchError, setSearchError] = useState('');
  const [searching, setSearching] = useState(false);
  const load = useCallback(async () => {
    const n = await fetch('/api/video/nodes'); if (n.ok) setNodes((await n.json()) as Node[]);
    const j = await fetch('/api/video/jobs'); if (j.ok) setJobs((await j.json()) as Job[]);
  }, []);
  const findJob = async () => {
    const id = jobIdSearch.trim();
    if (!id) return;
    setSearching(true); setSearchError('');
    try {
      const response = await fetch(`/api/video/jobs/${encodeURIComponent(id)}`);
      if (!response.ok) throw new Error(response.status === 404 ? 'Nie znaleziono renderu o podanym ID.' : `Nie udało się wyszukać renderu (${response.status}).`);
      setSearchedJob(await response.json() as Job);
    } catch (error) { setSearchedJob(null); setSearchError((error as Error).message); }
    finally { setSearching(false); }
  };
  useEffect(() => { load().catch(() => undefined); const t = setInterval(() => load().catch(() => undefined), 8000); return () => clearInterval(t); }, [load]);
  const active = jobs.filter((j) => j.status === 'RENDERING' || j.status === 'QUEUED' || j.status === 'PROCESSING').length;
  return (
    <div>
      <h2>Render Farm</h2>
      <p className="muted">Węzły renderujące + lokalne joby wideo · odświeżanie co 8 s · aktywne: {active}</p>
      <form className="render-farm-search" onSubmit={(event) => { event.preventDefault(); void findJob(); }}>
        <label htmlFor="render-farm-job-id">Znajdź render po ID</label>
        <input id="render-farm-job-id" value={jobIdSearch} onChange={(event) => setJobIdSearch(event.target.value)} placeholder="np. vid_69ef91d7b1764ee8b0ba" />
        <button className="btn btn--primary" type="submit" disabled={searching || !jobIdSearch.trim()}>{searching ? 'Szukam…' : 'Szukaj renderu'}</button>
        {searchError && <span role="alert" className="error">{searchError}</span>}
      </form>
      <div className="card">
        <div className="card__header">Węzły ({nodes.length})</div>
        <div className="render-farm-nodes-wrap">
          <table className="render-farm-nodes">
            <thead><tr><th>Node</th><th>Stan</th><th>Zadania</th><th>Zasoby</th><th>Ostatni heartbeat</th></tr></thead>
            <tbody>
              {nodes.map((n) => (
                <tr key={n.id}>
                  <td>
                    <strong>{n.name}</strong>
                    <div className="muted" style={{ fontSize: 12 }}>{n.hostname ?? n.id}{n.version ? ` · v${n.version}` : ''}</div>
                    {n.capabilities.length > 0 && <div className="muted" style={{ fontSize: 11 }}>{n.capabilities.join(' · ')}</div>}
                  </td>
                  <td>
                    <span className={nodeBadge(n.operationalStatus)}>{nodeStatusLabel(n.operationalStatus)}</span>
                    {(n.operationalStatus === 'WAITING_FOR_RESOURCES' || n.operationalStatus === 'SHORTS_ONLY') && <div className="render-farm-wait-hint">{renderFarmWaitHint(n)}</div>}
                  </td>
                  <td>
                    <div>{n.activeJobs}/{n.maxConcurrency} aktywne · long: {n.availableSlots == null ? '—' : n.availableSlots} · short: {n.shortAvailableSlots == null ? '—' : n.shortAvailableSlots} slotów</div>
                    {n.currentJobs.map((job) => <div key={job.id} className="muted" style={{ fontSize: 11 }}>{job.module} · <code style={{ overflowWrap: 'anywhere' }}>{job.id}</code> · {job.status} {job.progress}%</div>)}
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    RAM {formatBytes(n.memoryFreeBytes)} · dysk {formatBytes(n.diskFreeBytes)}
                    <div>Load (1 min): {n.loadAverage == null ? '—' : n.loadAverage.toFixed(2)}</div>
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>{n.lastHeartbeat ? formatTimestamp(n.lastHeartbeat) : '—'}</td>
                </tr>
              ))}
              {nodes.length === 0 && <tr><td colSpan={5} className="muted">Brak węzłów.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <div className="card" style={{ marginTop: 12 }}>
        <div className="card__header">Lokalne joby ({jobs.length}{searchedJob && !jobs.some((job) => job.id === searchedJob.id) ? ' + wyszukany' : ''})</div>
        <div className="render-farm-jobs-wrap">
          <table className="table render-farm-jobs">
            <colgroup><col /><col /><col /><col /><col /><col /><col /><col /></colgroup>
            <thead><tr><th>Job</th><th>Główny moduł</th><th>Status</th><th>Mode</th><th>Progress</th><th>Renderer</th><th>Data i czas</th><th>Error</th></tr></thead>
            <tbody>
              {[...(searchedJob && !jobs.some((job) => job.id === searchedJob.id) ? [searchedJob] : []), ...jobs].map((j) => (
                <tr key={j.id}>
                  <td style={{ fontSize: 12 }}><code style={{ overflowWrap: 'anywhere' }}>{j.id}</code></td>
                  <td>{j.module}</td>
                  <td><span className={badge(j.status)}>{j.status}</span></td>
                  <td>{j.videoMode}</td><td>{j.progress}%</td>
                  <td>{j.rendererId ?? '—'}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{formatTimestamp(j.createdAt)}</td>
                  <td className="render-farm-error-cell">{j.error ? <details className="render-farm-error"><summary title={j.error}>{errorPreview(j.error)}</summary><pre>{j.error}</pre></details> : <span className="muted">—</span>}</td>
                </tr>
              ))}
              {jobs.length === 0 && !searchedJob && <tr><td colSpan={8} className="muted">Brak jobów.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
